/* eslint max-lines: ["error", {max: 330}] -- splash animation with art area, parallax, delayed entrance, and exit */

import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { scheduleOnRN } from "react-native-worklets";

import splashCard1 from "@/assets/images/components/onboarding/splash/card1.png";
import splashCard2 from "@/assets/images/components/onboarding/splash/card2.png";
import splashCard3 from "@/assets/images/components/onboarding/splash/card3.png";
import splashCard4 from "@/assets/images/components/onboarding/splash/card4.png";
import splashCard5 from "@/assets/images/components/onboarding/splash/card5.png";
import splashEllipse from "@/assets/images/components/onboarding/splash/ellipse.png";
import { OnboardingV2TestID } from "@/constants/TestID";
import type { ImageProps } from "@/ui/core";
import { Button, Image, Text } from "@/ui/core";
import { Box, VStack } from "@/ui/layout";
import { AnimatedSection } from "@/ui/layout/animated/AnimatedSection";
import type { MotionParallaxSensor } from "@/ui/layout/animated/MotionParallaxLayer";
import {
  MotionParallaxLayer,
  useMotionParallax,
} from "@/ui/layout/animated/MotionParallaxLayer";
import { PopInView } from "@/ui/layout/animated/PopInView";
import { ScaleInView } from "@/ui/layout/animated/ScaleInView";
import type { BoxProps } from "@/ui/layout/Box";
import { ColorToken, LayoutToken } from "@/ui/theme/tokens";
import { joinTestIDs } from "@/utils/string.util";

enum SplashCardTiming {
  Cadence = 380,
  PopDuration = 820,
  Start = 600,
}

enum SplashCopyDelay {
  DelayAfterArt = 40,
  DelayBetweenElements = 450,
}

enum SplashDuration {
  Exit = 460,
  SplashEllipse = 900,
}

enum SplashExitDistance {
  Default = -10,
}

enum ArtAreaSize {
  CardHeight = 105,
  CardWidth = 102,
  EllipseHeight = 190,
  EllipseWidth = 252,
}

type PositionProps = Pick<BoxProps, "height" | "left" | "top" | "width">;

interface SplashIllustration {
  position: PositionProps;
  source: ImageProps["uriSource"];
}

const ArtAreaTestID = joinTestIDs(OnboardingV2TestID.Splash, "art");

const SharedCardPosition: PositionProps = {
  height: "39%",
  width: "33%",
};

const SplashIllustrations: SplashIllustration[] = [
  {
    position: { ...SharedCardPosition, left: "32%", top: "2%" },
    source: splashCard1,
  },
  {
    position: { ...SharedCardPosition, left: "63%", top: "24%" },
    source: splashCard2,
  },
  {
    position: { ...SharedCardPosition, left: "51%", top: "57%" },
    source: splashCard3,
  },
  {
    position: { ...SharedCardPosition, left: "13%", top: "57%" },
    source: splashCard4,
  },
  {
    position: { ...SharedCardPosition, left: "1%", top: "24%" },
    source: splashCard5,
  },
];

const SplashIllustrationCount = SplashIllustrations.length;

// Animation sequence:
//   200ms  Ellipse scales in behind the cards
//   600ms  Cards pop in clockwise (380ms apart), last card settles ~2940ms
//   2980ms Title floats up (40ms delay after art)
//   3430ms Subtitle floats up (450ms delay)
//   3880ms Button floats up (450ms delay)

const CardsEnd =
  SplashCardTiming.Start +
  (SplashIllustrations.length - 1) * SplashCardTiming.Cadence +
  SplashCardTiming.PopDuration;

const SplashDelay = {
  ActionButton:
    CardsEnd +
    SplashCopyDelay.DelayAfterArt +
    SplashCopyDelay.DelayBetweenElements * 2,
  CardCadence: SplashCardTiming.Cadence,
  CardStart: SplashCardTiming.Start,
  Ellipse: 200,
  Subtitle:
    CardsEnd +
    SplashCopyDelay.DelayAfterArt +
    SplashCopyDelay.DelayBetweenElements,
  Title: CardsEnd + SplashCopyDelay.DelayAfterArt,
} as const;

const EllipseParallaxDepth = -0.2;

interface SplashIllustrationViewProps {
  illustration: SplashIllustration;
  index: number;
  motion: MotionParallaxSensor | null;
}

const SplashIllustrationView = ({
  illustration,
  index,
  motion,
}: SplashIllustrationViewProps) => {
  const parallaxDepth = (index + 1) / SplashIllustrations.length;
  return (
    <Box
      height={illustration.position.height}
      key={joinTestIDs(ArtAreaTestID, `card-${index + 1}`)}
      left={illustration.position.left}
      position="absolute"
      top={illustration.position.top}
      width={illustration.position.width}
    >
      <PopInView
        delay={SplashDelay.CardStart + index * SplashDelay.CardCadence}
      >
        <MotionParallaxLayer depth={parallaxDepth} motion={motion}>
          <Image
            contentFit="contain"
            size={{
              height: ArtAreaSize.CardHeight,
              width: ArtAreaSize.CardWidth,
            }}
            testID={joinTestIDs(ArtAreaTestID, `card-${index + 1}`)}
            uriSource={illustration.source}
          />
        </MotionParallaxLayer>
      </PopInView>
    </Box>
  );
};

const SplashArtArea = () => {
  const motion = useMotionParallax();

  return (
    <Box
      alignSelf="center"
      aspectRatio={308 / 268}
      marginBottom={22}
      maxWidth="100%"
      position="relative"
      testID={ArtAreaTestID}
      width={308}
    >
      <Box
        height="70.9%"
        left="7.66%"
        position="absolute"
        top="19.03%"
        width="81.82%"
      >
        <ScaleInView delay={SplashDelay.Ellipse}>
          <MotionParallaxLayer depth={EllipseParallaxDepth} motion={motion}>
            <Image
              contentFit="contain"
              size={{
                height: ArtAreaSize.EllipseHeight,
                width: ArtAreaSize.EllipseWidth,
              }}
              testID={joinTestIDs(ArtAreaTestID, "ellipse")}
              uriSource={splashEllipse}
            />
          </MotionParallaxLayer>
        </ScaleInView>
      </Box>
      {/* eslint-disable-next-line max-params -- map callback requires index for delay computation */}
      {SplashIllustrations.map((illustration, index) => {
        return (
          <SplashIllustrationView
            illustration={illustration}
            index={index}
            key={joinTestIDs(ArtAreaTestID, `card-${index + 1}`)}
            motion={motion}
          />
        );
      })}
    </Box>
  );
};

const baseTestID = OnboardingV2TestID.Splash;

interface OnboardingSplashScreenProps {
  appUser: string;
  onContinuePress: () => void;
  patientName?: string;
}

const SplashContent = {
  cta: "Get started",
  patientSubtitle:
    "Whether you're sure you need help or still deciding, your care team meets you where you are. Next, we'll get to know you and set up your team so that treatment can start.",
};

const getSplashTitle = (name: string) =>
  `Lasting recovery is possible, ${name}.`;

const getSplashSubtitle = (patientName?: string) =>
  patientName
    ? `You already know something isn't right, and that's what ${patientName}'s care team is here for. Next, we review their info and get their team ready to start treatment soon.`
    : SplashContent.patientSubtitle;

const ExitEasing = Easing.out(Easing.ease);

const OnboardingSplashScreen = ({
  appUser,
  onContinuePress,
  patientName,
}: OnboardingSplashScreenProps) => {
  const { bottom } = useSafeAreaInsets();
  const isReducedMotion = useReducedMotion();
  const exitOpacity = useSharedValue(1);
  const exitTranslateY = useSharedValue(0);

  const handleContinuePress = () => {
    if (isReducedMotion) {
      onContinuePress();
      return;
    }
    const config = { duration: SplashDuration.Exit, easing: ExitEasing };
    exitTranslateY.value = withTiming(SplashExitDistance.Default, config);
    exitOpacity.value = withTiming(0, config, (hasFinished) => {
      if (hasFinished) scheduleOnRN(onContinuePress);
    });
  };

  const exitStyle = useAnimatedStyle(() => ({
    opacity: exitOpacity.value,
    transform: [{ translateY: exitTranslateY.value }],
  }));

  return (
    <Animated.View style={[{ flex: 1, width: "100%" }, exitStyle]}>
      <VStack
        flex={1}
        paddingBottom={bottom}
        paddingHorizontal={LayoutToken.OnboardingScreenHorizontalPadding}
        width="100%"
      >
        <VStack alignItems="center" flex={1} gap={14} justifyContent="center">
          <SplashArtArea />
          <AnimatedSection delay={SplashDelay.Title}>
            <Text
              color={ColorToken.Primary90Variant}
              testID={joinTestIDs(baseTestID, "title")}
              textAlign="center"
              variant="h2"
            >
              {getSplashTitle(appUser)}
            </Text>
          </AnimatedSection>
          <AnimatedSection delay={SplashDelay.Subtitle}>
            <Text
              color={ColorToken.NeutralSecondary}
              testID={joinTestIDs(baseTestID, "subtitle")}
              textAlign="center"
              variant="body"
            >
              {getSplashSubtitle(patientName)}
            </Text>
          </AnimatedSection>
        </VStack>
        <AnimatedSection delay={SplashDelay.ActionButton}>
          <Button
            isFullWidth
            onPress={handleContinuePress}
            testID={joinTestIDs(baseTestID, "continue")}
            title={SplashContent.cta}
          />
        </AnimatedSection>
      </VStack>
    </Animated.View>
  );
};

export {
  getSplashSubtitle,
  getSplashTitle,
  OnboardingSplashScreen,
  SplashContent,
  SplashIllustrationCount,
};
